/**
 * bellState.ts — Default bell instrument preference (local-only).
 *
 * BellId is now BellInstrumentId from @retreat/core so the setting maps
 * directly to a synthesized instrument in BellLibrary.
 *
 * Migration: old fake IDs (bowl-large, bowl-small, soft-chime, meal, wake)
 * are remapped to the nearest real instrument on first read.
 */

import type { BellInstrumentId } from "@retreat/core";
import { STORAGE_KEYS } from "../constants";

const BELL_KEY = STORAGE_KEYS.DEFAULT_BELL;
const DEFAULT_BELL: BellInstrumentId = "rin";

// Re-export for consumers that still type-import BellId
export type BellId = BellInstrumentId;

/** Old catalogue IDs → nearest synthesized instrument */
const LEGACY_MIGRATION: Record<string, BellInstrumentId> = {
  "bowl-large": "singing_bowl",
  "bowl-small": "tingsha",
  "soft-chime": "rin",
  "meal":       "rin",
  "wake":       "rin",
};

export function getDefaultBell(): BellInstrumentId {
  try {
    const stored = localStorage.getItem(BELL_KEY);
    if (!stored) return DEFAULT_BELL;
    // Migrate legacy catalogue IDs silently
    if (stored in LEGACY_MIGRATION) {
      const migrated = LEGACY_MIGRATION[stored];
      localStorage.setItem(BELL_KEY, migrated);
      return migrated;
    }
    return stored as BellInstrumentId;
  } catch {
    return DEFAULT_BELL;
  }
}

export function setDefaultBell(id: BellInstrumentId): void {
  try {
    localStorage.setItem(BELL_KEY, id);
  } catch {
    // localStorage unavailable
  }
}
